(function () {
    // Theme configuration for each plan tier (Bold vector lines + Bold pill outlines)
    const TIER_THEMES = {
        'element': {
            url: 'https://cdn.cosmocrypt.com/lottie-and-json/element.json',
            primaryRgb: [0.2, 0.85, 0.6], // Emerald Mint (#34d399)
            secondaryRgb: [0.38, 0.88, 0.68], // Bright Mint
            badgeClass: 'border-2 border-emerald-400/60 bg-emerald-500/[0.08] text-emerald-300 shadow-[0_0_25px_rgba(16,185,129,0.25)]',
            glowColor: 'rgba(16, 185, 129, 0.9)'
        },
        'catalyst': {
            url: 'https://cdn.cosmocrypt.com/lottie-and-json/catalyst.json',
            primaryRgb: [0.38, 0.65, 0.98], // Sky Indigo (#60a5fa)
            secondaryRgb: [0.22, 0.74, 0.97], // Electric Cyan (#38bdf8)
            badgeClass: 'border-2 border-sky-400/60 bg-sky-500/[0.08] text-sky-300 shadow-[0_0_25px_rgba(56,189,248,0.25)]',
            glowColor: 'rgba(56, 189, 248, 0.9)'
        },
        'matrix': {
            url: 'https://cdn.cosmocrypt.com/lottie-and-json/matrix.json',
            primaryRgb: [0.66, 0.33, 0.97], // Cyber Violet (#a855f7)
            secondaryRgb: [0.75, 0.52, 0.98], // Light Violet (#c084fc)
            badgeClass: 'border-2 border-purple-400/60 bg-purple-500/[0.08] text-purple-300 shadow-[0_0_25px_rgba(168,85,247,0.25)]',
            glowColor: 'rgba(168, 85, 247, 0.9)'
        },
        'quantum': {
            url: 'https://cdn.cosmocrypt.com/lottie-and-json/quantum.json',
            primaryRgb: [0.98, 0.75, 0.14], // Radiant Amber Gold (#fbbf24)
            secondaryRgb: [0.99, 0.88, 0.35], // Bright Cyber Gold (#fde047)
            badgeClass: 'border-2 border-amber-400/70 bg-amber-500/[0.08] text-amber-300 shadow-[0_0_25px_rgba(245,158,11,0.35)]',
            glowColor: 'rgba(245, 158, 11, 0.95)'
        }
    };

    const lottieBlobCache = {};

    async function getTintedLottieUrl(tierKey) {
        const config = TIER_THEMES[tierKey] || TIER_THEMES.element;
        if (lottieBlobCache[tierKey]) return lottieBlobCache[tierKey];

        try {
            const res = await fetch(config.url);
            const json = await res.json();

            // Calculate ideal bold stroke width based on canvas resolution (430px canvas vs 240px canvas)
            const canvasW = json.w || 430;
            const targetStroke = canvasW > 300 ? 38 : 24;

            // Deep recursive color & stroke replacement
            function processNode(node) {
                if (!node || typeof node !== 'object') return;

                // Stroke element
                if (node.ty === 'st') {
                    if (node.w && typeof node.w.k === 'number') {
                        node.w.k = targetStroke;
                    }
                    if (node.c && Array.isArray(node.c.k)) {
                        if (node.cl === 'secondary' || node.nm === '.secondary') {
                            node.c.k = [...config.secondaryRgb, 1];
                        } else {
                            node.c.k = [...config.primaryRgb, 1];
                        }
                    }
                }
                // Fill element
                if (node.ty === 'fl') {
                    if (node.c && Array.isArray(node.c.k)) {
                        if (node.cl === 'secondary' || node.nm === '.secondary') {
                            node.c.k = [...config.secondaryRgb, 1];
                        } else if (node.cl === 'primary' || node.nm === '.primary' || node.nm === 'primary.design') {
                            node.c.k = [...config.primaryRgb, 1];
                        }
                    }
                }

                // Recurse children
                for (let k in node) {
                    if (typeof node[k] === 'object') processNode(node[k]);
                }
            }

            processNode(json);

            // Also do string replacement on any remaining color arrays in expressions
            let tintedText = JSON.stringify(json)
                .replace(/0\.07[0-9]*,0\.07[0-9]*,0\.19[0-9]*/g, `${config.primaryRgb[0]},${config.primaryRgb[1]},${config.primaryRgb[2]}`)
                .replace(/0\.03[0-9]*,0\.65[0-9]*,0\.54[0-9]*/g, `${config.secondaryRgb[0]},${config.secondaryRgb[1]},${config.secondaryRgb[2]}`);

            const blob = new Blob([tintedText], { type: 'application/json' });
            const blobUrl = URL.createObjectURL(blob);
            lottieBlobCache[tierKey] = blobUrl;
            return blobUrl;
        } catch (e) {
            console.warn('Lottie tint fetch fallback:', e);
            return config.url;
        }
    }

    // Pre-cache all 4 tinted Lotties immediately so changes/updates are 0ms instant
    Object.keys(TIER_THEMES).forEach(k => getTintedLottieUrl(k));

    function getUserProfileData(userParam, cloudDataParam) {
        let cosmoName = '';
        let email = '';
        let plan = '';
        let isLoggedIn = false;

        // 1. Check explicit logout signal
        let isExplicitLogout = false;
        try {
            if (localStorage.getItem('explicit_logout') === 'true' || sessionStorage.getItem('explicit_logout') === 'true' || window._userExplicitlyLoggedOut === true) {
                isExplicitLogout = true;
            }
        } catch (e) { }

        // 2. Auth Object Discovery
        const authObj = userParam ||
            ((window.suiteAuth && window.suiteAuth.currentUser) ? window.suiteAuth.currentUser :
                (window.parent && window.parent.suiteAuth && window.parent.suiteAuth.currentUser) ? window.parent.suiteAuth.currentUser :
                    (window.top && window.top.suiteAuth && window.top.suiteAuth.currentUser) ? window.top.suiteAuth.currentUser : null);

        // 3. User Data Object Targets
        const targets = [
            cloudDataParam,
            window.userData,
            (window.parent && window.parent !== window) ? window.parent.userData : null,
            (window.top && window.top !== window) ? window.top.userData : null
        ];

        let validUserData = null;
        for (const t of targets) {
            if (t && typeof t === 'object' && (t.email || t.first || t.name || t.cosmoName || t.displayName || t.isVerified)) {
                validUserData = t;
                break;
            }
        }

        // 4. Decrypt AES userStoreEnc if validUserData is missing
        if (!validUserData) {
            try {
                const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
                if (enc && typeof CryptoJS !== 'undefined') {
                    const secFn = window._getAppSecret || (window.parent && window.parent._getAppSecret) || (window.top && window.top._getAppSecret);
                    const sec = (typeof secFn === 'function') ? secFn() : null;
                    if (sec) {
                        const bytes = CryptoJS.AES.decrypt(enc, sec);
                        const decStr = bytes.toString(CryptoJS.enc.Utf8);
                        if (decStr) {
                            const dec = JSON.parse(decStr);
                            if (dec && (dec.email || dec.first || dec.name || dec.cosmoName || dec.isVerified)) {
                                validUserData = dec;
                                targets.push(dec);
                            }
                        }
                    }
                }
            } catch (e) { }
        }

        // 5. Scan target objects for name, email and plan
        for (const t of targets) {
            if (t && typeof t === 'object') {
                if (t.first || t.last) {
                    const fullFromFirstLast = ((t.first || '') + ' ' + (t.last || '')).trim();
                    if (fullFromFirstLast) cosmoName = fullFromFirstLast;
                }
                if (!cosmoName && (t.cosmoName || t.customName || t.displayName || t.name || t.fullName || t.firstName)) {
                    cosmoName = t.cosmoName || t.customName || t.displayName || t.name || t.fullName || t.firstName;
                }
                if (t.email && !email) email = t.email;
                if (t.subscription && t.subscription.plan && !plan) plan = t.subscription.plan;
                if (t.plan && !plan) plan = t.plan;
            }
        }

        // 6. Inspect local / session storage keys for profile fallback
        if (!cosmoName || !email || !plan) {
            try {
                const rawProf = localStorage.getItem('cosmo_user_profile') || localStorage.getItem('user_profile') || localStorage.getItem('userData') || localStorage.getItem('userStore');
                if (rawProf) {
                    const parsed = JSON.parse(rawProf);
                    if (parsed.first || parsed.last) {
                        const fullP = ((parsed.first || '') + ' ' + (parsed.last || '')).trim();
                        if (fullP) cosmoName = fullP;
                    }
                    if (!cosmoName && (parsed.cosmoName || parsed.customName || parsed.displayName || parsed.fullName || parsed.name || parsed.firstName)) {
                        cosmoName = parsed.cosmoName || parsed.customName || parsed.displayName || parsed.fullName || parsed.name || parsed.firstName;
                    }
                    if (!email && parsed.email) email = parsed.email;
                    if (!plan && parsed.plan) plan = parsed.plan;
                    if (!plan && parsed.subscription && parsed.subscription.plan) plan = parsed.subscription.plan;
                }
            } catch (e) { }

            try {
                if (!cosmoName) {
                    cosmoName = localStorage.getItem('user_cosmo_name') ||
                        localStorage.getItem('user_custom_name') ||
                        localStorage.getItem('user_display_name') ||
                        localStorage.getItem('user_name') ||
                        sessionStorage.getItem('user_name') || '';
                }
                if (!email) {
                    email = localStorage.getItem('user_email') || sessionStorage.getItem('user_email') || '';
                }
                if (!plan) {
                    plan = localStorage.getItem('user_subscription') ||
                        localStorage.getItem('last_notified_subscription') ||
                        localStorage.getItem('current_plan') ||
                        localStorage.getItem('cosmo_user_plan') || '';
                }
            } catch (e) { }
        }

        // 7. Fallback to auth object credentials
        if (authObj) {
            if (!cosmoName && authObj.displayName && !authObj.displayName.includes('@')) {
                cosmoName = authObj.displayName;
            }
            if (!email && authObj.email) email = authObj.email;
        }

        // 8. Derive cosmoName from email if still blank
        if (!cosmoName && email && email.includes('@')) {
            const handle = email.split('@')[0];
            cosmoName = handle.charAt(0).toUpperCase() + handle.slice(1);
        }

        // 9. Determine Login Status
        if (!isExplicitLogout && (authObj || validUserData || cosmoName || (email && email.includes('@')) || sessionStorage.getItem('__cosmo_session_active') === 'true' || ((sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc')) && (sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc')).length > 20))) {
            isLoggedIn = true;
        }

        return {
            cosmoName: cosmoName.trim(),
            email: email.trim(),
            plan: plan.trim(),
            isLoggedIn,
            validUserData
        };
    }

    function getActivePlanKey(cloudDataParam, validUserData) {
        let rawSub = '';

        if (cloudDataParam && cloudDataParam.plan) {
            rawSub = cloudDataParam.plan;
        } else if (cloudDataParam && cloudDataParam.subscription && cloudDataParam.subscription.plan) {
            rawSub = cloudDataParam.subscription.plan;
        }

        if (!rawSub && window.COSMO_CRYPT_PLAN && window.COSMO_CRYPT_PLAN.plan) {
            rawSub = window.COSMO_CRYPT_PLAN.plan;
        }
        if (!rawSub && window.cosmo_PLAN && window.cosmo_PLAN.plan) {
            rawSub = window.cosmo_PLAN.plan;
        }

        if (!rawSub) {
            try {
                rawSub = localStorage.getItem('user_subscription') ||
                    localStorage.getItem('last_notified_subscription') ||
                    localStorage.getItem('current_plan') ||
                    localStorage.getItem('cosmo_user_plan');
            } catch (e) { }
        }

        if (!rawSub || rawSub === 'element' || rawSub === 'normal') {
            try {
                const targets = [
                    window.userData,
                    validUserData,
                    (window.parent && window.parent !== window) ? window.parent.userData : null,
                    (window.top && window.top !== window) ? window.top.userData : null
                ];
                for (const t of targets) {
                    if (t && t.subscription && t.subscription.plan) {
                        rawSub = t.subscription.plan;
                        break;
                    }
                    if (t && t.plan) {
                        rawSub = t.plan;
                        break;
                    }
                }
            } catch (e) { }
        }

        if (!rawSub || rawSub === 'element' || rawSub === 'normal') {
            try {
                const subEnc = sessionStorage.getItem('cosmocrypt_sub_enc') || localStorage.getItem('cosmocrypt_sub_enc');
                const secFn = window._getAppSecret || (window.parent && window.parent._getAppSecret) || (window.top && window.top._getAppSecret);
                const secret = typeof secFn === 'function' ? secFn() : null;
                if (subEnc && secret && typeof CryptoJS !== 'undefined') {
                    const b = CryptoJS.AES.decrypt(subEnc, secret);
                    const decStr = b.toString(CryptoJS.enc.Utf8);
                    if (decStr) {
                        const dec = JSON.parse(decStr);
                        if (dec && dec.plan) rawSub = dec.plan;
                    }
                }
            } catch (e) { }
        }

        rawSub = String(rawSub || 'element').toLowerCase().trim();

        if (rawSub.includes('quantum') || rawSub.includes('ultra') || rawSub.includes('enterprise') || rawSub.includes('unlimited') || rawSub === '100') {
            return 'quantum';
        }
        if (rawSub.includes('matrix') || rawSub.includes('pro') || rawSub.includes('professional') || rawSub === '50') {
            return 'matrix';
        }
        if (rawSub.includes('catalyst') || rawSub.includes('velocity') || rawSub.includes('max') || rawSub.includes('essential') || rawSub === '25') {
            return 'catalyst';
        }
        return 'element';
    }

    window.updateLandingPersonalization = async function (user, cloudData, overrideHour) {
        try {
            const headlineEl = document.getElementById('landing-hero-headline');
            const subtitleEl = document.getElementById('landing-hero-subtitle');
            const ctaBtn = document.getElementById('landing-cta-schedule-consult');
            const badgeEl = document.getElementById('landing-status-badge');
            const badgeText = document.getElementById('landing-status-badge-text');
            const lottieEl = document.getElementById('landing-plan-lottie');
            const desktopNavBtn = document.getElementById('landing-nav-signup');
            const mobileNavBtn = document.getElementById('mobile-nav-signup');

            // 1. Resolve Profile & Login Status
            const prof = getUserProfileData(user, cloudData);

            // 2. Determine Active Plan Tier
            let activePlan = 'element';
            if (prof.isLoggedIn) {
                activePlan = getActivePlanKey(cloudData, prof.validUserData);
            }
            if (!TIER_THEMES[activePlan]) activePlan = 'element';
            const theme = TIER_THEMES[activePlan];

            // 3. Determine Time of Day (support manual override for testing)
            const hour = typeof overrideHour === 'number' ? overrideHour : new Date().getHours();
            let timeGreeting = 'Good evening';
            let enclaveStatus = 'Twilight Sentinel Online';

            if (hour >= 5 && hour < 12) {
                // 5:00 AM – 11:59 AM
                timeGreeting = 'Good morning';
                enclaveStatus = 'Daybreak Enclave Sync';
            } else if (hour >= 12 && hour < 17) {
                // 12:00 PM – 4:59 PM
                timeGreeting = 'Good afternoon';
                enclaveStatus = 'Peak Node Operations';
            } else if (hour >= 17 && hour < 21) {
                // 5:00 PM – 8:59 PM
                timeGreeting = 'Good evening';
                enclaveStatus = 'Twilight Sentinel Online';
            } else {
                // 9:00 PM – 4:59 AM
                timeGreeting = 'Good night';
                enclaveStatus = 'Secure Night Vault Active';
            }

            // 4. Get Tinted Lottie URL
            const tintedLottieUrl = await getTintedLottieUrl(activePlan);

            // 5. Update Navbar Buttons
            if (desktopNavBtn) {
                desktopNavBtn.textContent = prof.isLoggedIn ? 'Account' : 'Sign Up';
            }
            if (mobileNavBtn) {
                const textEl = mobileNavBtn.querySelector('.id-text');
                const descEl = mobileNavBtn.querySelector('.id-desc');
                if (textEl) textEl.textContent = prof.isLoggedIn ? 'Account' : 'Sign Up';
                if (descEl) descEl.textContent = prof.isLoggedIn ? 'Manage your credentials' : 'Create registry credentials';
            }

            // 6. Update Landing Hero Section
            if (prof.isLoggedIn && prof.cosmoName) {
                const rawFirst = (prof.cosmoName.split(' ')[0] || prof.cosmoName).trim();
                const firstName = rawFirst ? (rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1).toLowerCase()) : 'User';

                // Update Lottie animations with plan tint and glow
                if (lottieEl) {
                    lottieEl.setAttribute('src', tintedLottieUrl);
                    lottieEl.style.width = '20px';
                    lottieEl.style.height = '20px';
                    lottieEl.style.filter = `drop-shadow(0 0 6px ${theme.glowColor})`;
                }

                // Apply glowing theme styling to status badge
                const baseBadgeClasses = "inline-flex items-center gap-2.5 px-4 py-1.5 sm:px-4.5 sm:py-1.5 rounded-full border-2 text-[11px] sm:text-xs font-semibold tracking-[0.1em] uppercase mb-6 backdrop-blur-xl select-none transition-all duration-500";
                if (badgeEl) {
                    badgeEl.className = `${baseBadgeClasses} ${theme.badgeClass}`;
                }

                if (badgeText) badgeText.textContent = enclaveStatus;

                // Update Headline
                if (headlineEl) {
                    headlineEl.className = "font-general-sans text-[48px] sm:text-[80px] md:text-[110px] lg:text-[130px] font-normal leading-[1.04] tracking-[-0.024em] select-none text-foreground m-0";
                    headlineEl.innerHTML = `${timeGreeting}, <br class="hidden sm:inline"/><span class="bg-clip-text text-transparent" style="background-image: linear-gradient(to left, #6366f1, #a855f7, #fcd34d)">${firstName}</span>`;
                }

                // Update Subtitle
                if (subtitleEl) {
                    subtitleEl.textContent = 'Your 8 segmented credential vaults and gateway nodes are active and secured with zero-knowledge encryption.';
                }

                // Update CTA Button
                if (ctaBtn) {
                    ctaBtn.textContent = 'Open Console';
                }
            } else {
                // Guest fallback
                const fallbackUrl = await getTintedLottieUrl('element');
                if (lottieEl) {
                    lottieEl.setAttribute('src', fallbackUrl);
                    lottieEl.style.width = '20px';
                    lottieEl.style.height = '20px';
                    lottieEl.style.filter = `drop-shadow(0 0 6px ${TIER_THEMES.element.glowColor})`;
                }
                const defaultBadgeClass = "inline-flex items-center gap-2.5 px-4 py-1.5 sm:px-4.5 sm:py-1.5 rounded-full bg-white/[0.04] border-2 border-white/20 text-[11px] sm:text-xs font-semibold tracking-[0.1em] uppercase text-white/90 mb-6 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.5)] select-none transition-all duration-500";
                if (badgeEl) badgeEl.className = defaultBadgeClass;

                if (badgeText) badgeText.textContent = 'End-to-End Encrypted Secure Registry';

                if (headlineEl) {
                    headlineEl.className = "font-general-sans text-[76px] sm:text-[130px] md:text-[180px] lg:text-[220px] font-normal leading-[1.02] tracking-[-0.024em] select-none text-foreground m-0";
                    headlineEl.innerHTML = `Cosmo <span class="bg-clip-text text-transparent" style="background-image: linear-gradient(to left, #6366f1, #a855f7, #fcd34d)">Crypt</span>`;
                }
                if (subtitleEl) {
                    subtitleEl.innerHTML = `Cosmo Crypt is an end-to-end encrypted password manager and zero-trust credential vault. <br class="hidden sm:inline" /> Take complete control of your digital identity, API keys, and sensitive data.`;
                }
                if (ctaBtn) {
                    ctaBtn.textContent = 'Launch Console';
                }
            }
        } catch (err) {
            console.warn('Personalization update error:', err);
        }
    };

    // ⚡ Reactive Live Event Listeners for 0ms Instant Sync
    let _personalizationDebounce = null;
    function schedulePersonalizationUpdate() {
        if (_personalizationDebounce) return;
        _personalizationDebounce = requestAnimationFrame(() => {
            _personalizationDebounce = null;
            window.updateLandingPersonalization();
        });
    }

    const syncEvents = [
        'cosmoProfileUpdated',
        'cosmoUserDataUpdated',
        'userDataUpdated',
        'safeStorageChange',
        'storage',
        'cosmoTemplateStoreUpdated',
        'cosmo_analytics_updated',
        'cosmoCryptPlanChanged',
        'cosmoPlanChanged',
        'cosmo_auth_state_changed',
        'focus',
        'visibilitychange'
    ];

    syncEvents.forEach(evt => {
        window.addEventListener(evt, schedulePersonalizationUpdate);
        try {
            if (window.parent && window.parent !== window) {
                window.parent.addEventListener(evt, schedulePersonalizationUpdate);
            }
        } catch (e) { }
        try {
            if (window.top && window.top !== window) {
                window.top.addEventListener(evt, schedulePersonalizationUpdate);
            }
        } catch (e) { }
    });

    // Run initial check immediately and on DOM load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            window.updateLandingPersonalization();
        });
    } else {
        window.updateLandingPersonalization();
    }
    window.addEventListener('load', function () {
        window.updateLandingPersonalization();
    });
})();
